import React, { useEffect, useRef } from 'react'
import { Form } from 'react-bootstrap'
import { useField, useFormikContext } from 'formik'
import PropTypes from 'prop-types'
import RBDateRangePicker from 'react-bootstrap-daterangepicker'
import 'bootstrap-daterangepicker/daterangepicker.css'
import { formatDate } from '../../utils/date'
import classNames from 'classnames'
import moment from 'moment-timezone'

const SingleDatePicker = ({
  formGroupProps: {
    className: formGroupClassName,
    ...otherFormGroupProps
  } = {},
  children,
  id,
  label,
  labelProps,
  renderChildren,
  size,
  className,
  spanClassName = '',
  showTooltip,
  customErrorMessage,
  showErrorMessage,
  type,
  placeholder,
  removeFormikField,
  onFocus,
  onValueChange,
  onShow,
  minDate,
  disabled,
  name,
  position,
  opens,
  inputStyle,
  minimumDate = null,
  maximumDate = null,
  pickerClass = '',
  displayFlex = true,
  isInvalid: isInvalidProp = false,
  inputStyle: inputStyleProp,
  errorMessage,
  ...rest
}) => {
  const picker = useRef(null)
  const { setFieldValue, values } = useFormikContext()
  const [field, meta] = useField({
    ...rest,
    ...{ type: 'text' },
    name,
  })

  useEffect(() => {
    if (picker.current && picker.current.$picker) {
      const fieldValue = field.value || values[name]
      if (fieldValue) {
        picker.current.setStartDate(formatDate(fieldValue))
        picker.current.setEndDate(formatDate(fieldValue))
        picker.current.$picker.val(formatDate(fieldValue))
      } else {
        picker.current.$picker.val('')
        picker.current.setStartDate(formatDate(moment()))
        picker.current.setEndDate(formatDate(moment()))
      }
    }
  }, [field.value, values, name])

  const renderLabel = () => {
    if (label) {
      return (
        <Form.Label className='text-uppercase' {...labelProps}>
          {label}
        </Form.Label>
      )
    }
    return null
  }

  const onApply = (e, datePicker) => {
    if (moment(datePicker.startDate).isValid()) {
      const nextValue = datePicker.startDate.format('MM/DD/YYYY')
      setFieldValue(name, nextValue)
      datePicker.element.val(nextValue)
      if (onValueChange) {
        onValueChange(nextValue)
      }
    } else {
      datePicker.element.val('')
      setFieldValue(name, null)
      if (onValueChange) {
        onValueChange('')
      }
    }
  }

  const handleChange = (e) => {
    if (moment(e.target.value).isValid()) {
      setFieldValue(name, e.target.value)
      if (onValueChange) {
        onValueChange(e.target.value)
      }
    }
  }

  const handleKeyDown = (e) => {
    // Prevent backspace from partially deleting dates
    // If backspace is pressed and there's a value, clear the entire date instead
    const fieldValue = field.value || values[name]
    if (e.key === 'Backspace' && fieldValue && fieldValue !== '') {
      e.preventDefault()
      // Clear the entire date field
      setFieldValue(name, '')
      if (onValueChange) {
        onValueChange('')
      }
      if (picker.current && picker.current.$picker) {
        picker.current.$picker.val('')
      }
    }
    // Also prevent Delete key from partially deleting dates
    if (e.key === 'Delete' && fieldValue && fieldValue !== '') {
      e.preventDefault()
      // Clear the entire date field
      setFieldValue(name, '')
      if (onValueChange) {
        onValueChange('')
      }
      if (picker.current && picker.current.$picker) {
        picker.current.$picker.val('')
      }
    }
  }

  // Library's built-in drops:'auto' uses parent scrollHeight, not the visible
  // viewport — so near the bottom of an overflow container it still opens down
  // and forces a scrollbar. Reposition against the viewport after show.
  const handleShow = (e, datePicker) => {
    if (position === 'auto' && datePicker?.element && datePicker?.container) {
      const inputRect = datePicker.element[0].getBoundingClientRect()
      const pickerHeight = datePicker.container.outerHeight() || 0
      const spaceBelow = window.innerHeight - inputRect.bottom
      const spaceAbove = inputRect.top
      datePicker.drops =
        spaceBelow < pickerHeight && spaceAbove > spaceBelow ? 'up' : 'down'
      datePicker.move()
    }
    if (typeof onShow === 'function') {
      onShow(e, datePicker)
    }
  }

  let initialDatePickerSettings = {
    singleDatePicker: true,
    showDropdowns: true,
    autoUpdateInput: false,
    locale: {
      format: 'MM/DD/YYYY',
    },
    applyButtonClasses: 'btn-saffron font-montserrat',
    cancelButtonClasses: 'btn-black font-montserrat',
    drops: position,
    opens: opens,
    autoApply: true,
  }
  if (minimumDate) {
    initialDatePickerSettings.minDate = minimumDate
  }
  if (maximumDate) {
    initialDatePickerSettings.maxDate = maximumDate
  }
  if (pickerClass) {
    initialDatePickerSettings.parentEl = pickerClass
  }

  const showInvalid =
    isInvalidProp ||
    (meta.touched && !!meta.error) ||
    (showErrorMessage && !!(errorMessage || meta.error))
  const mergedInputStyle = {
    border: '1px solid #000',
    background: '#fff',
    ...inputStyleProp,
    ...(showInvalid ? { border: '1px solid #dc3545' } : {}),
  }

  return (
    <span
      className={classNames(
        'd-flex position-relative align-items-center',
        spanClassName,
      )}>
      <div
        className={classNames(
          { 'd-lg-flex': displayFlex },
          'align-items-center w-100',
        )}>
        {renderLabel()}
        <RBDateRangePicker
          ref={picker}
          onApply={onApply}
          onShow={handleShow}
          initialSettings={initialDatePickerSettings}>
          <input
            {...field}
            autoComplete={'off'}
            placeholder='MM/DD/YYYY'
            disabled={disabled}
            type='text'
            value={field.value || ''}
            className={classNames(
              'form-control',
              { 'is-valid': meta.touched && !meta.error && !isInvalidProp },
              { 'is-invalid': showInvalid },
              className,
            )}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            name={name}
            isValid={meta.touched && !meta.error && !isInvalidProp}
            isInvalid={showInvalid}
            style={mergedInputStyle}
          />
        </RBDateRangePicker>
        {showErrorMessage && (errorMessage || meta.error) ? (
          <Form.Text className='text-muted font-montserrat font-semibold font-12 w-100'>
            <span className='font-montserrat font-semibold transition d-flex justify-content-end text-error px-1'>
              {errorMessage || meta.error}
            </span>
          </Form.Text>
        ) : null}
      </div>
    </span>
  )
}

SingleDatePicker.defaultProps = {
  showTooltip: false,
  removeFormikField: false,
  type: 'text',
  showErrorMessage: false,
  disabled: false,
  opens: 'right',
  position: 'down',
  inputStyle: {
    border: '1px solid #000',
    background: '#fff',
    //height: '42px',
  },
}

SingleDatePicker.propTypes = {
  removeFormikField: PropTypes.bool,
  showErrorMessage: PropTypes.bool,
  isInvalid: PropTypes.bool,
  onValueChange: PropTypes.func,
  position: PropTypes.oneOf(['down', 'up', 'auto']),
  opens: PropTypes.oneOf(['left', 'right', 'center']),
}

export default SingleDatePicker
